<script setup lang="ts">
import { TextField } from '@haskoning/vue-component-library'
import { useScenarioFormComposable } from '@/components/scenario-form/composable/useScenarioFormComposable'
import type { ScenarioFormMixinProps } from '@/components/scenario-form/type/type'
import { storeToRefs } from 'pinia'
import { useProjectStore } from '@/stores/project'
import { computed, ref } from 'vue'
import CustomHelpTextToolTip from '@/components/form/CustomHelpTextToolTip.vue'
import { useRoute } from 'vue-router'
import { docUrl } from '@/components/scenario-form/constants/constants'

interface GeneralFormProps extends ScenarioFormMixinProps {
  projectId?: number
  scenarioId?: number
}

const props = defineProps<GeneralFormProps>()
const emit = defineEmits(['updateTabCompletion', 'changeTab'])
const { currentItem: currentProject } = storeToRefs(useProjectStore())

const validationLoading = ref(false)
const validatingFileName = ref('')

const isComplete = computed(() => {
  const allFilesUploaded = fileFieldNames.every((field) => {
    return !scenarioStore.fields?.[field]?.required || !!uploadStatus.value[field]
  })

  const allOtherFieldsFilled = otherProjectInformationFields.every((field) => {
    return !scenarioStore.fields?.[field]?.required || !!currentItem.value?.[field]
  })

  return allFilesUploaded && allOtherFieldsFilled
})

const checkAndUpdateCompletion = async () => {
  emit('updateTabCompletion', 'PROJECT_DETAILS', isComplete.value)
}

const handleFieldUpdate = async () => {
  if (!currentItem.value) return

  await scenarioStore.updateScenario(currentItem.value.id, currentItem.value, fileFieldNames)
  await checkAndUpdateCompletion()
}

const { scenarioStore, currentItem, isValid, formRef, disabled, isReadOnly, hasHelpText } =
  useScenarioFormComposable(props, emit, () => isComplete.value)
defineExpose({ formRef })

const uploadedFileUrls = ref<Record<string, string>>({})
const projectInformationFields = [
  'name',
  'origin_type',
  'project',
  'destination_type',
  'disruption_type'
]
const fileFields = [
  {
    name: 'networks',
    image: '/networks.png',
    description: `
      Road network is a base layer used to generate routes between each origin (the starting point) point and the closest destination (e.g. a resource / facility / service / infrastructure). The routes can be generated based on two principles: the shortest distance or the shortest travel time.<br>
      Required format of the dataset: .shp (line layer)<br>
      Baseline requirements: A network dataset consists of multiple line elements representing individual road segments. During the preparation phase, it is important that the entire network is being split at road intersections in order to run correctly. This is a critical step during preparation, otherwise the tool will generate incorrect routes or errors.<br>
      Additional requirements: In order to generate routes based on the shortest travel distance, the dataset must contain information about time needed to travel over each road segment. More information about this step can be found in the link below. The information about the time it takes to travel over the road segment should be stored under the column name ‘Minutes’.<br>
      Ensure that each road link has an original name (usually called the objectID or FID). The original names should be stored under the column name ‘ObjectIDroad’.
    `
  },
  {
    name: 'origin',
    image: '/origin.png',
    description: `
    Population is a point layer illustrating the spatial distribution of a chosen group of people. The dataset may represent an entire population or a specific group of people (e.g., only women or people within a certain age group). A user is free to determine who the target group should be and what is a sufficient level of granularity for such a dataset.<br>
    The population layer is used to provide starting points for route calculations.<br>
    Required format of the dataset: .shp (point layer)<br>
    Baseline requirements: The layer consists of points. If there is no population size aggregated to each point, the tool will treat them equally in size.<br>
    Additional requirements: Each point may have an aggregated size of population. This information adds value to the final visualization of the number of people affected.
  `
  },
  {
    name: 'destinations',
    image: '/destinations.png',
    description: `
    Destinations is a point layer representing the spatial distribution of a given type of resource, facility, service, or critical infrastructure of the user’s choice. Each point serves as a destination for route calculations.<br>
    All points within the destination layer need to belong to the same category, meaning that they need to be comparable. For example, if a user has a dataset containing healthcare facilities, they should be filtered down to the same type, e.g., to hospitals or family doctors only. There should not be a mix of different facilities (e.g., dental facilities and hospitals), because the tool will not be able to differentiate them.<br>
    Required format of the dataset: .shp (point layer)<br>
    Baseline requirements: The layer consists of points.
  `
  },
  {
    name: 'disruptions',
    image: '/disruptions.png',
    description: `
    Disruptions are an optional vector layer used to clip inaccessible parts of the network in order to exclude them from route calculations. Disruptions can represent any kind of inaccessibility to the roads – a flood event or a collapsed or closed bridge. The layer can be created either from hazard maps or can be drawn manually.<br>
    In case of raster hazard maps, they need to be vectorized first before being inserted into the tool. Moreover, in the case of flood maps, a user should consider the water depth from which roads are seen as inaccessible and apply those thresholds before vectorizing a flood map.<br>
    In case of other types of disruptions, such as the closure of a specific road segment, it is sufficient to create a single polygon overlapping with that segment.<br>
    Required format of the dataset: .shp (polygon layer)<br>
    Baseline requirements: Elements within the disruptions layer need to overlap with road segments that are supposed to be clipped.
  `
  }
]

const extra_path = 'content'

function navigateToDoc() {
  const url = docUrl ? docUrl + extra_path : '/docs/content'
  window.open(url, '_blank')
}

window.navigateToDoc = navigateToDoc

const fileFieldNames = fileFields.map((field) => field.name)

const otherProjectInformationFields = projectInformationFields.filter(
  (field) => field !== 'project'
)

const route = useRoute()
const projectName = computed(() => route.query.projectName || currentProject.value?.name)

const uploadStatus = ref(
  fileFieldNames.reduce(
    (status, field) => {
      status[field] = String(currentItem.value?.[field] || '')
      return status
    },
    {} as Record<string, string>
  )
)

const handleFileRemove = async (type: string) => {
  try {
    if (currentItem.value) {
      const formData = new FormData()
      formData.append(type, '')
      await scenarioStore.updateFile(currentItem.value.id, formData)
    }

    uploadStatus.value[type] = ''
    uploadedFileUrls.value[type] = ''
  } catch (error) {
    console.error('Error removing file:', error)
  }
}

const handleFileUpload = async (type: string, event: Event) => {
  const input = event.target as HTMLInputElement
  if (!input.files || input.files.length === 0) {
    uploadStatus.value[type] = `No file selected.`
    return
  }

  const file = input.files[0]
  const validExtensions = ['.zip']
  const fileExtension = file.name.slice(((file.name.lastIndexOf('.') - 1) >>> 0) + 2)

  if (!validExtensions.includes(`.${fileExtension}`)) {
    uploadStatus.value[type] = `Invalid file type. Please upload a zip file.`
    return
  }

  uploadStatus.value[type] = file.name
  uploadedFileUrls.value[type] = URL.createObjectURL(file)

  validationLoading.value = true
  validatingFileName.value = file.name

  if (!currentItem.value) {
    console.error('currentItem is undefined or does not have the expected structure.')
    validationLoading.value = false
    return
  }

  const formData = new FormData()
  formData.append(type, file)

  try {
    await scenarioStore.updateFile(currentItem.value.id, formData)
    uploadStatus.value[type] = file.name
    validationLoading.value = false
  } catch (error: any) {
    uploadStatus.value[type] = ''
    uploadedFileUrls.value[type] = ''
    validationLoading.value = false
  }
}

const extractOriginalFileName = (fileNameOrUrl: string): string => {
  const fileName = fileNameOrUrl.split('/').pop() || ''
  return fileName
}
const fileUploadRules = (key: string) => [
  (value: string) => !scenarioStore.fields?.[key]?.required || !!value || 'File is required.'
]
const acceptedFormats = computed(() => '.zip')

const getFileUrl = (fieldName: string): string | undefined => {
  if (currentItem.value?.[`${fieldName}_sas_link`]) {
    return currentItem.value[`${fieldName}_sas_link`] as string | undefined
  }

  if (currentItem.value?.[fieldName]) {
    const value = currentItem.value[fieldName]
    return typeof value === 'string' ? value : ''
  }

  if (uploadedFileUrls.value[fieldName]) {
    return uploadedFileUrls.value[fieldName]
  }
  return undefined
}

const currentScenarioItem = computed({
  get: () => currentItem.value ?? {},
  set: (value) => {
    currentItem.value = Object.keys(value).length === 0 ? null : (value as typeof currentItem.value)
  }
})
</script>

<template>
  <v-form ref="formRef" v-model="isValid" :disabled="disabled" :readonly="isReadOnly">
    <v-card elevation="0" flat>
      <v-card-title class="header-font d-flex justify-space-between align-center">
        Data Selection
      </v-card-title>
      <v-card-subtitle class="description-font">
        Need help?
        <span
          class="text-primary"
          @click="navigateToDoc"
          style="
            color: #009eab;
            font-size: 1rem;
            text-decoration: none;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
          "
        >
          Find out how to complete this step here.
          <span style="font-size: 1em; vertical-align: middle">
            <v-icon small>mdi-open-in-new</v-icon>
          </span>
        </span>
      </v-card-subtitle>

      <div class="file-upload-grid">
        <v-row dense>
          <v-col cols="3" v-for="field in fileFields" :key="field.name">
            <div class="file-upload-title">
              <CustomHelpTextToolTip
                v-if="scenarioStore.fields && scenarioStore.fields[field.name]?.help_text"
                :text="field.description || ''"
                :image="field.image"
                :title="scenarioStore.fields[field.name]?.label || field.name"
                class="file-title-tooltip"
              />
              <span class="file-title-tooltip"></span>
              <span class="file-title">
                {{
                  (scenarioStore.fields && scenarioStore.fields[field.name]?.label) || field.name
                }}
              </span>
            </div>

            <v-card flat outlined>
              <v-card-text class="file-upload-container">
                <p v-if="uploadStatus[field.name]" class="file-name-container">
                  <a
                    v-if="getFileUrl(field.name)"
                    :href="getFileUrl(field.name)"
                    target="_blank"
                    class="file-name"
                    :download="
                      !getFileUrl(field.name)?.startsWith('http')
                        ? uploadStatus[field.name]
                        : undefined
                    "
                  >
                    {{ extractOriginalFileName(uploadStatus[field.name]) }}
                  </a>
                </p>

                <label v-if="!uploadStatus[field.name] && !isReadOnly" class="file-upload-label">
                  <input
                    type="file"
                    :accept="acceptedFormats"
                    @change="handleFileUpload(field.name, $event)"
                    class="file-input"
                  />
                  <span class="upload-text">
                    <span class="mdi mdi-upload-outline"></span> Upload File
                  </span>
                </label>
                <label
                  v-else-if="!isReadOnly"
                  class="file-upload-label remove-file-btn"
                  @click="handleFileRemove(field.name)"
                >
                  <span class="upload-text">
                    <span class="mdi mdi-delete-outline"></span> Remove File
                  </span>
                </label>

                <div v-else-if="isReadOnly" class="upload-text">
                  <span class="mdi mdi-lock-outline"></span> Read-Only
                </div>

                <p class="accepted-formats-note">Accepted Formats: {{ acceptedFormats }}</p>
              </v-card-text>
            </v-card>

            <v-text-field
              v-model="uploadStatus[field.name]"
              :rules="fileUploadRules(field.name)"
              :name="`file-upload-${field.name}`"
              hide-details
              style="display: none"
            />
          </v-col>
        </v-row>
      </div>

      <v-card-text>
        <span class="sub-header-font"> Simulation Details </span>
        <div style="margin-left: -7px">
          <v-row align="center" no-gutters>
            <v-col cols="12" md="8" lg="4" class="d-flex align-center ml-1">
              <CustomHelpTextToolTip
                v-if="hasHelpText('project')"
                :text="(scenarioStore.fields && scenarioStore.fields['project']?.help_text) || ''"
              />
              <span :class="hasHelpText('project') ? 'px-2' : 'px-0'">Project Name</span>
            </v-col>

            <v-col cols="12" md="8" lg="4" class="pt-0">
              <TextField
                :field="
                  scenarioStore.fields?.['project'] ?? {
                    type: '',
                    required: false,
                    read_only: false,
                    label: '',
                    help_text: '',
                    max_length: 0,
                    field_type: '',
                    parameter: ''
                  }
                "
                :model-value="{ project: projectName }"
                :no-help-text="true"
                :readonly="true"
              />
            </v-col>
          </v-row>

          <!-- Other Fields -->
          <v-row
            align="center"
            no-gutters
            v-for="field in otherProjectInformationFields"
            :key="field"
          >
            <v-col cols="12" md="8" lg="4" class="d-flex align-center ml-1">
              <CustomHelpTextToolTip
                v-if="hasHelpText(field)"
                :text="(scenarioStore.fields && scenarioStore.fields[field]?.help_text) || ''"
              />
              <span :class="hasHelpText(field) ? 'px-2' : 'px-0'">
                {{ scenarioStore.fields && scenarioStore.fields[field]?.label }}
              </span>
            </v-col>

            <v-col cols="12" md="8" lg="4" class="pt-0">
              <TextField
                :field="
                  scenarioStore.fields?.[field] ?? {
                    type: '',
                    required: false,
                    read_only: false,
                    label: '',
                    help_text: '',
                    max_length: 0,
                    field_type: '',
                    parameter: ''
                  }
                "
                v-model="currentScenarioItem"
                :no-help-text="true"
                :readonly="isReadOnly"
                @update="handleFieldUpdate"
              />
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-form>
  <v-dialog v-model="validationLoading" persistent width="320">
    <v-card class="d-flex flex-column align-center pa-6">
      <v-progress-circular indeterminate color="primary" size="42" />
      <div class="mt-4 text-center">
        <div class="mb-2">Validating file...</div>
        <div class="text-caption text-grey-600">{{ validatingFileName }}</div>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.file-upload-grid {
  margin: 20px;
}

.file-upload-title {
  text-align: center;
  margin-bottom: 10px;
}

.file-upload-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 150px;
  text-align: center;
  position: relative;
}

.file-upload-label {
  cursor: pointer;
  background-color: transparent;
  border: none;
  color: #009eab;
  font-size: 16px;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.file-upload-label:hover {
  color: #009eab;
}

.file-input {
  display: none;
}

.upload-text {
  display: inline-block;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  color: #009eab;
}

.file-upload-grid :deep(.v-card) {
  background-color: #f5f5f5;
}

.accepted-formats-note {
  position: absolute;
  bottom: 10px;
  font-size: 12px;
  line-height: 1.2;
  color: black;
  text-align: center;
}

.file-name-container {
  width: 100%;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  margin-bottom: 10px;
}

.file-name {
  color: #009eab;
  font-weight: bold;
  text-decoration: none;
}

.file-name:hover {
  text-decoration: underline;
}

.remove-file-btn {
  background-color: transparent;
  border: none;
  color: #009eab;
  font-size: 16px;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.remove-file-btn:hover {
  color: #009eab;
}

.file-title-tooltip {
  margin-left: 5px;
}
</style>
